import { GuidelineFragment } from "src/state/gql/_types"
import { FormSchema } from "src/utils/forms"

export type AgencyOperationsAdminVenueBookingGuidelineCreateFragment = Omit<GuidelineFragment, 'id' | 'type' | 'status' | 'created_at' | 'updated_at' | 'created_by_user' | 'updated_by_user' | 'guideline_status' | 'guideline_type' | 'guideline_role' | 'document' | 'parent_guideline' | 'child_guidelines' | 'target_booking_status_enum' | 'target_booking_type_enum' | 'tenant_id' | 'venue_id' | 'area_id' | 'talent_id' | 'booking_id' | 'parent_guideline_id' | 'deleted' | 'created_by' | 'updated_by' | 'tenant_confirmed' | 'venue_confirmed' | 'talent_confirmed' | 'tenant_completed' | 'venue_completed' | 'talent_completed'>

export const AgencyOperationsAdminVenueBookingGuidelineCreateSchema: FormSchema<AgencyOperationsAdminVenueBookingGuidelineCreateFragment> = {
    id: "guideline",
    title: "Guideline",
    groups: {
        default: {
            label: 'Default'
        }
    },
    fields: {
        role: {
            group: 'default',
            label: "Role",
            type: "guideline-role-select",
            grid: 4,
        },
        target_booking_type: {
            group: 'default',
            label: "Booking Type",
            type: "booking-type-select",
            grid: 4,
        },
        target_booking_status: {
            group: 'default',
            label: "Booking Status",
            type: "booking-status-select",
            grid: 4,
        },
        name: {
            group: 'default',
            label: "Type",
            type: "venue-guideline-title-select",
            grid: 12,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        description: {
            group: 'default',
            label: "Guidelines",
            type: "textarea",
            grid: 12,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        information: {
            group: 'default',
            label: "Information",
            type: "textarea",
            grid: 12,
            divider: true
        },
        tenant_required: {
            group: 'default',
            label: "Agency Confirmation Required",
            type: "boolean",
            grid: 4,
        },
        venue_required: {
            group: 'default',
            label: "Venue Confirmation Required",
            type: "boolean",
            grid: 4,
        },
        talent_required: {
            group: 'default',
            label: "Talent Confirmation Required",
            type: "boolean",
            grid: 4,
            divider: true
        },
        metadata: {
            group: 'default',
            label: "Questions to Ask",
            type: "guideline-metadata-builder",
            grid: 12,
            divider: true,
        },
        document_id: {
            group: 'default',
            label: "Document",
            type: "guideline-document-upload",
            grid: 12,
        },
    }
}

export type AgencyOperationsAdminVenueBookingGuidelineUpdateFragment = Omit<GuidelineFragment, 'id' | 'type' | 'created_at' | 'updated_at' | 'created_by_user' | 'updated_by_user' | 'guideline_status' | 'guideline_type' | 'guideline_role' | 'document' | 'parent_guideline' | 'child_guidelines' | 'target_booking_status_enum' | 'target_booking_type_enum' | 'tenant_id' | 'venue_id' | 'area_id' | 'talent_id' | 'booking_id' | 'parent_guideline_id' | 'deleted' | 'created_by' | 'updated_by'>

export const AgencyOperationsAdminVenueBookingGuidelineUpdateSchema: FormSchema<AgencyOperationsAdminVenueBookingGuidelineUpdateFragment> = {
    id: "guideline",
    title: "Guideline",
    groups: {
        default: {
            label: 'Default'
        },
        tracking: {
            label: 'Tracking'
        }
    },
    fields: {
        status: {
            group: 'default',
            label: "Status",
            type: "guideline-status-select",
            grid: 12,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        role: {
            group: 'default',
            label: "Role",
            type: "guideline-role-select",
            grid: 4,
        },
        target_booking_type: {
            group: 'default',
            label: "Booking Type",
            type: "booking-type-select",
            grid: 4,
        },
        target_booking_status: {
            group: 'default',
            label: "Booking Status",
            type: "booking-status-select",
            grid: 4,
        },
        name: {
            group: 'default',
            label: "Type",
            type: "venue-guideline-title-select",
            grid: 12,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        description: {
            group: 'default',
            label: "Guidelines",
            type: "textarea",
            grid: 12,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        information: {
            group: 'default',
            label: "Information",
            type: "textarea",
            grid: 12,
            divider: true
        },
        tenant_required: {
            group: 'default',
            label: "Agency Confirmation Required",
            type: "boolean",
            grid: 4,
        },
        venue_required: {
            group: 'default',
            label: "Venue Confirmation Required",
            type: "boolean",
            grid: 4,
        },
        talent_required: {
            group: 'default',
            label: "Talent Confirmation Required",
            type: "boolean",
            grid: 4,
            divider: true
        },
        document_id: {
            group: 'default',
            label: "Document",
            type: "guideline-document-upload",
            grid: 12
        },
        tenant_confirmed: {
            group: 'tracking',
            label: "Agency Confirmed",
            type: "boolean",
            grid: 4,
        },
        venue_confirmed: {
            group: 'tracking',
            label: "Venue Confirmed",
            type: "boolean",
            grid: 4,
        },
        talent_confirmed: {
            group: 'tracking',
            label: "Talent Confirmed",
            type: "boolean",
            grid: 4,
            divider: true
        },
        tenant_completed: {
            group: 'tracking',
            label: "Agency Completed",
            type: "boolean",
            grid: 4,
        },
        venue_completed: {
            group: 'tracking',
            label: "Venue Completed",
            type: "boolean",
            grid: 4,
        },
        talent_completed: {
            group: 'tracking',
            label: "Talent Completed",
            type: "boolean",
            grid: 4,
        },
    }
}
